// Shared model-list state for the editor-first shell (C0-R). Mounted ONCE inside
// AppShell so the sidebar AND /browse read the same lists — a mutation in either
// reflects in both. It also centralizes the freemium wall: create/unarchive that
// hit the active-model limit raise ONE UpgradePrompt (rendered at the shell level)
// with the archive-swap flow, so the conversion surface is identical everywhere.
//
// Lives in the main bundle (no cad-react/konva), inside the Router, so it can
// navigate to the editor when a model is created/opened.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { LimitExceededError, api } from '../api/client';
import type { LimitExceededPayload, MeStats, Model } from '../api/types';
import { errorMessage } from '../lib/errors';
import { toast } from '../components/ui/sonner';

// A queued action that hit the active-models wall; after the user archives a
// model to free a slot, this is what we run.
type Pending = { kind: 'create'; name: string } | { kind: 'unarchive'; id: string } | null;

interface ModelsState {
  active: Model[];
  archived: Model[];
  stats: MeStats | null;
  loading: boolean;
  error: string | null;
  /** Refetch active + archived + stats. */
  refresh: () => Promise<void>;
  /** Create + open a model. Returns the model, or null if it hit the wall (the
   *  UpgradePrompt is raised) / failed. */
  create: (name?: string) => Promise<Model | null>;
  rename: (id: string, name: string) => Promise<void>;
  archive: (id: string) => Promise<void>;
  /** Unarchive (may hit the wall → UpgradePrompt). */
  unarchive: (id: string) => Promise<void>;
  remove: (id: string, opts?: { currentModelId?: string }) => Promise<void>;
  // Freemium wall (shell renders the UpgradePrompt from these).
  limit: LimitExceededPayload | null;
  swapBusy: boolean;
  dismissLimit: () => void;
  swapArchive: (archiveId: string) => Promise<void>;
}

const ModelsContext = createContext<ModelsState | null>(null);

export function ModelsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [active, setActive] = useState<Model[]>([]);
  const [archived, setArchived] = useState<Model[]>([]);
  const [stats, setStats] = useState<MeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [limit, setLimit] = useState<LimitExceededPayload | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [swapBusy, setSwapBusy] = useState(false);
  const mounted = useRef(true);
  // Set true on setup (not just construction) so StrictMode's mount→unmount→mount
  // doesn't leave it permanently false (which would skip every setState and wedge
  // the provider in `loading`). The cleanup marks it false on a real unmount.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const [a, ar, s] = await Promise.all([
      api.listModels('active'),
      api.listModels('archived'),
      api.getStats(),
    ]);
    if (!mounted.current) return;
    setActive(a);
    setArchived(ar);
    setStats(s);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await refresh();
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
  }, [refresh]);

  const openModel = useCallback(
    (m: Model) => {
      void refresh();
      navigate(`/editor/${m.id}`);
    },
    [navigate, refresh],
  );

  const create = useCallback(
    async (name = 'Untitled'): Promise<Model | null> => {
      setError(null);
      try {
        const model = await api.createModel({ name: name.trim() || 'Untitled' });
        openModel(model);
        return model;
      } catch (err) {
        if (err instanceof LimitExceededError) {
          setPending({ kind: 'create', name: name.trim() || 'Untitled' });
          setLimit(err.payload);
          return null;
        }
        setError(errorMessage(err));
        toast.error(errorMessage(err));
        return null;
      }
    },
    [openModel],
  );

  const rename = useCallback(async (id: string, name: string) => {
    try {
      await api.renameModel(id, name);
      const apply = (xs: Model[]) => xs.map((m) => (m.id === id ? { ...m, name } : m));
      setActive(apply);
      setArchived(apply);
    } catch (err) {
      setError(errorMessage(err));
      toast.error(errorMessage(err));
    }
  }, []);

  const archive = useCallback(
    async (id: string) => {
      try {
        await api.archiveModel(id);
        await refresh();
        toast.success('Model archived');
      } catch (err) {
        setError(errorMessage(err));
        toast.error(errorMessage(err));
      }
    },
    [refresh],
  );

  const unarchive = useCallback(
    async (id: string) => {
      try {
        await api.unarchiveModel(id);
        await refresh();
      } catch (err) {
        if (err instanceof LimitExceededError) {
          setPending({ kind: 'unarchive', id });
          setLimit(err.payload);
          return;
        }
        setError(errorMessage(err));
        toast.error(errorMessage(err));
      }
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string, opts?: { currentModelId?: string }) => {
      try {
        await api.deleteModel(id);
        await refresh();
        toast.success('Model deleted');
        // If the open model was just deleted, re-resolve the landing target.
        if (opts?.currentModelId === id) navigate('/', { replace: true });
      } catch (err) {
        setError(errorMessage(err));
        toast.error(errorMessage(err));
      }
    },
    [navigate, refresh],
  );

  const dismissLimit = useCallback(() => {
    setLimit(null);
    setPending(null);
  }, []);

  // Archive-swap: free a slot, then run the queued action (create or unarchive).
  const swapArchive = useCallback(
    async (archiveId: string) => {
      if (!pending) return;
      setSwapBusy(true);
      setError(null);
      try {
        await api.archiveModel(archiveId);
        if (pending.kind === 'create') {
          const model = await api.createModel({ name: pending.name });
          setLimit(null);
          setPending(null);
          openModel(model);
          return;
        }
        await api.unarchiveModel(pending.id);
        await refresh();
        setLimit(null);
        setPending(null);
      } catch (err) {
        if (err instanceof LimitExceededError) setLimit(err.payload);
        else {
          setError(errorMessage(err));
          toast.error(errorMessage(err));
        }
      } finally {
        if (mounted.current) setSwapBusy(false);
      }
    },
    [openModel, pending, refresh],
  );

  const value = useMemo<ModelsState>(
    () => ({
      active,
      archived,
      stats,
      loading,
      error,
      refresh,
      create,
      rename,
      archive,
      unarchive,
      remove,
      limit,
      swapBusy,
      dismissLimit,
      swapArchive,
    }),
    [
      active,
      archived,
      stats,
      loading,
      error,
      refresh,
      create,
      rename,
      archive,
      unarchive,
      remove,
      limit,
      swapBusy,
      dismissLimit,
      swapArchive,
    ],
  );

  return <ModelsContext.Provider value={value}>{children}</ModelsContext.Provider>;
}

export function useModels(): ModelsState {
  const ctx = useContext(ModelsContext);
  if (!ctx) throw new Error('useModels must be used within <ModelsProvider>');
  return ctx;
}
